import { useState, useEffect } from 'react'
import NotificationManager, { NotificationSettings as NotificationSettingsType } from '@/utils/notification-manager'

export default function NotificationSettings() {
  const [settings, setSettings] = useState<NotificationSettingsType | null>(null)
  const [notificationPermission, setNotificationPermission] = useState<NotificationPermission>('default')

  const notificationManager = NotificationManager.getInstance()

  useEffect(() => {
    // 加载当前设置
    const currentSettings = notificationManager.getSettings()
    setSettings(currentSettings)

    // 检查通知权限
    if ('Notification' in window) {
      setNotificationPermission(Notification.permission)
    }
  }, [])

  const updateSetting = (key: keyof NotificationSettingsType, value: any) => {
    if (!settings) return

    const newSettings = { ...settings, [key]: value }
    setSettings(newSettings)
    notificationManager.updateSettings({ [key]: value })
  }

  const requestNotificationPermission = async () => {
    const permission = await notificationManager.requestPermission()
    setNotificationPermission(permission)
  }

  const testNotification = () => {
    // 创建一个测试消息
    const testMessage = {
      sender_id: 'test_user',
      senderName: '系统测试',
      conversation_id: 'test_conversation',
      session_type: 'direct' as const,
      content: '这是一条测试通知消息',
      decryptedContentData: {
        content: '这是一条测试通知消息'
      }
    } as any

    notificationManager.showNotification(testMessage)
  }

  const resetSettings = () => {
    notificationManager.resetSettings()
    const defaultSettings = notificationManager.getSettings()
    setSettings(defaultSettings)
  }

  if (!settings) {
    return <div className="p-4">加载中...</div>
  }

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">通知设置</h2>

        {/* 通知权限状态 */}
        <div className="mb-6 p-4 border rounded-lg">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-medium text-gray-900">浏览器通知权限</h3>
              <p className="text-sm text-gray-500">
                {notificationPermission === 'granted' && '✅ 已允许桌面通知'}
                {notificationPermission === 'denied' && '❌ 桌面通知已被拒绝'}
                {notificationPermission === 'default' && '⚠️ 尚未授权桌面通知'}
              </p>
            </div>
            {notificationPermission !== 'granted' && (
              <button
                onClick={requestNotificationPermission}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
              >
                请求权限
              </button>
            )}
          </div>
        </div>

        {/* 桌面通知设置 */}
        <div className="space-y-4">
          <h3 className="font-medium text-gray-900">桌面通知</h3>

          <div className="space-y-3">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={settings.desktopEnabled}
                onChange={(e) => updateSetting('desktopEnabled', e.target.checked)}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <span className="ml-2 text-sm text-gray-700">启用桌面通知</span>
            </label>

            <label className="flex items-center">
              <input
                type="checkbox"
                checked={settings.desktopSound}
                onChange={(e) => updateSetting('desktopSound', e.target.checked)}
                disabled={!settings.desktopEnabled}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded disabled:opacity-50"
              />
              <span className="ml-2 text-sm text-gray-700">桌面通知声音</span>
            </label>

            <label className="flex items-center">
              <input
                type="checkbox"
                checked={settings.desktopPreview}
                onChange={(e) => updateSetting('desktopPreview', e.target.checked)}
                disabled={!settings.desktopEnabled}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded disabled:opacity-50"
              />
              <span className="ml-2 text-sm text-gray-700">显示消息预览</span>
            </label>
          </div>
        </div>

        {/* 标签页通知设置 */}
        <div className="space-y-4 mt-6">
          <h3 className="font-medium text-gray-900">标签页提醒</h3>

          <div className="space-y-3">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={settings.titleFlash}
                onChange={(e) => updateSetting('titleFlash', e.target.checked)}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <span className="ml-2 text-sm text-gray-700">标签页标题闪烁</span>
            </label>

            <label className="flex items-center">
              <input
                type="checkbox"
                checked={settings.titleShowCount}
                onChange={(e) => updateSetting('titleShowCount', e.target.checked)}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <span className="ml-2 text-sm text-gray-700">标题显示未读数量</span>
            </label>
          </div>
        </div>

        {/* 声音设置 */}
        <div className="space-y-4 mt-6">
          <h3 className="font-medium text-gray-900">声音提醒</h3>

          <div className="space-y-3">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={settings.soundEnabled}
                onChange={(e) => updateSetting('soundEnabled', e.target.checked)}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <span className="ml-2 text-sm text-gray-700">启用声音提醒</span>
            </label>

            <div className="ml-6">
              <label className="block text-sm text-gray-700 mb-1">
                音量: {Math.round(settings.soundVolume * 100)}%
              </label>
              <input
                type="range"
                min="0"
                max="1"
                step="0.1"
                value={settings.soundVolume}
                onChange={(e) => updateSetting('soundVolume', parseFloat(e.target.value))}
                disabled={!settings.soundEnabled}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer disabled:opacity-50"
              />
            </div>
          </div>
        </div>

        {/* 操作按钮 */}
        <div className="flex space-x-4 mt-8 pt-6 border-t">
          <button
            onClick={testNotification}
            className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
            disabled={!settings.desktopEnabled || notificationPermission !== 'granted'}
          >
            测试通知
          </button>

          <button
            onClick={resetSettings}
            className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors"
          >
            重置为默认
          </button>
        </div>

        {/* 说明文字 */}
        <div className="mt-6 p-4 bg-blue-50 rounded-lg">
          <h4 className="font-medium text-blue-900 mb-2">使用说明</h4>
          <ul className="text-sm text-blue-800 space-y-1">
            <li>• 桌面通知需要浏览器权限，首次使用时会弹出授权请求</li>
            <li>• 标签页闪烁只在页面不可见时触发</li>
            <li>• 声音提醒会在收到新消息时播放简短的提示音</li>
          </ul>
        </div>
      </div>
    </div>
  )
}
